import { useEffect, useRef, useState } from "react";
import { LicenseInfo } from "./LicensingInfo";
import { AccountDetails } from "./AccountDetails";
import { AddressesDetails } from "./AddressesDetails";
import { ContactInfo } from "./ContactInfo";
import { Accordion, Spinner, AlertMessage } from "../";
import AccountIcon from "../../assets/images/MyProfile/profileIcon.svg";
import AddressesIcon from "../../assets/images/MyProfile/Addresses_Icon.svg";
import CreditCardIcon from '../../assets/images/MyProfile/CreditCardIcon.svg'
import { styled } from '@mui/material/styles';
import { Link } from "react-router-dom";
import { useUserInfoContext } from "../../contexts";
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, useTheme } from "@mui/material";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import MessageBanner from "../MessageBanner/MessageBanner";
import PaymentMethodsSection from "./PaymentMethodsSection";
import { getSAPUserByEmail } from "../../utils/UserService/UserApis";
import { getUserErrorMessages } from "../../utils/getErrorMessage";

const ProfileWrapper = styled('div')(({ theme: { breakpoints } }) => `
    height: 800px;
    display: flex;
    ${[breakpoints.down('sm')]} {
        flex-direction: column;
        height: auto;
    }
`);

const NavigationPanel = styled('div')(({ theme: { palette: { primary }, breakpoints }}) => `
    background-color: ${primary.main};
    height: auto;
    min-height: 800px;
    max-width: 400px;
    flex: 0 0 auto;
    position: relative;
    ${[breakpoints.down('sm')]} {
        width: 100%;
        padding: 40px;
        min-height: 80px;
    }
`);

const NavigationPanelLink = styled('a')(({isActive, theme: { palette: { neutral } }}) => `
    display: flex;
    align-items: center;
    font-size: 1rem;
    color: ${neutral.white};
    text-decoration: none;
    cursor: pointer;
    background-color:${ isActive ? '#225D84' : 'transparent'};
    padding:6px 60px;
`);

const NavIcon = styled('img')`
    margin-right: 10px;
`;

const AccountInfo = styled('div')`
    height: 100%;
    width:100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: scroll;
    scroll-behavior: smooth;
    &&::-webkit-scrollbar{
        display: none;
    }
`;

const StyledLink = styled(Typography)(({ theme: { palette: {secondary}, breakpoints } }) => `
  color: ${secondary.textLinks};
  ${[breakpoints.down('sm')]} {
    padding-top:20px;
  } 
`);

const accordionCustomStyle = {
    borderRadius: "0px !important",
    boxShadow: "none",
    border: "1px solid grey !important",
    margin: "0px !important",
    backgroundColor: "primary.main",
    color:"white",
    width:"100%",
    "& .MuiTypography-h5": {
        color:"white",
        display:"flex",
        alignItems: 'center'
    },
    "& .Mui-expanded": {
        backgroundColor: "#225D84",
    },
    "& .MuiAccordionSummary-expandIconWrapper": {
        marginRight:"40px"
    },
    "& .MuiAccordionSummary-root.Mui-expanded": {
        minHeight:"48px"
    },
    "& .MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded": {
        margin:"0px 0px 0px 40px",
    }
    
};

const PROFILE_CONTENT = {
    accountDetailsMenuLabel: "Account Details",
    addressesMenuLabel: "Addresses",
    contactUsLinkLabel: "customer support",
    headline: "Your Account Profile",
    menuWelcomeMessage: "Welcome",
    message: `We want to ensure that you receive the best possible assistance. 
              If you encounter any difficulties or if you need to update information, 
              please don\'t hesitate to contact our dedicated `,
    paymentMethodsMenuLabel: "Payment Methods"
};

const ProfileComponent = () => {
    const dispatch = useDispatch();
    const { userInfo } = useUserInfoContext() || '';
    const emailAddress = useSelector((state) => state.globalStates.emailAddress);
    const possiblePaths = ['addresses', 'account', 'payment-methods'];
    const initialNavLink = possiblePaths.find(path => window.location.pathname.includes(path)) || 'account';
    const [errorMsg, setErrorMsg] = useState('');
    const [navigationLink, setNavigationLink] = useState(initialNavLink);
    const [loader, setLoader] = useState(false);
    const theme = useTheme();
    const desktop = useMediaQuery(theme.breakpoints.up('sm'));
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const history = useHistory()

    const alertRef = useRef(null)
    const successAlertRef = useRef(null)
    
    const { globalErrorMessage } = useSelector(store=>store?.globalMessages);
    const generalApiErrorData = globalErrorMessage && globalErrorMessage.length && globalErrorMessage.filter((data) => data?.fields?.code === "GENERAL_API_ERROR");
    const generalApiError = generalApiErrorData && generalApiErrorData[0]?.fields?.message;
    const sapUserData = useSelector(store => store?.commerce?.sapUserData?.data);
    const locale = useSelector((state) => state.globalStates.locale);

    const renderText = (content)=> {
        if (typeof content === 'string') {
            return content;
        }
    }

    const MessageBannerForAffiliation = (
        <Box width={'100%'}>
            <MessageBanner 
                type="info"
                bannerContentComponent={
                    (
                        <Typography>
                        To find your 2024 Contract Affiliation, please select the contract affiliation button on the <Link to={`/${locale}/order-form`}>Quick Order Form.</Link>
                        </Typography>
                    )
                }
            />
        </Box>
    )

    const errorMsgHandler = (errorMsg) => {
        let message = errorMsg || generalApiError;
        setErrorMsg(message);
        alertRef.current?.openAlert(message);
    }

    const AccountSection = () => {
        return (<>
            {MessageBannerForAffiliation}
            <Box display='flex' width='80%' margin='60px auto 0 auto' flexDirection={{xs:'column', sm:'row'}} justifyContent='space-between'>
                <Box width={{xs:'100%', sm:'60%'}} pb={{xs:'24px', sm:'unset'}} display={'flex'} flexDirection={'column'} gap={'8px'}>
                    <Typography variant={'h2'} color='primary.main' textAlign='left' lineHeight='35px' id="headline">{PROFILE_CONTENT.headline}</Typography>
                    <Typography variant={'p1'} component="div" color='neutral.main' textAlign='left' lineHeight={{xs:'21px', sm:'24px'}}  m={{xs:'16px 0', sm:'unset'}} id="headlineMessage">
                        {renderText(PROFILE_CONTENT.message)}
                        <Link to={`/${locale}/contact-us`}>
                            <StyledLink variant={'link'}>{PROFILE_CONTENT.contactUsLinkLabel}
                            </StyledLink>
                        </Link>
                    </Typography>
                </Box>
            </Box>
            <ContactInfo
                contactName={sapUserData?.name}
                contactEmail={sapUserData?.email}
                jobTitle={sapUserData?.jobTitle}
                contactRole={sapUserData?.position}
            />
            <LicenseInfo
                userInfo={sapUserData}
            />
            <AccountDetails
                userInfo={sapUserData}
            />
        </>)
    }

    const handlePageSelection = (URL) => {
        setNavigationLink(URL)
        if(URL === 'account') history.push(`/${locale}/my-profile`)
        else history.push(`/${locale}/my-profile/${URL}`)
    }

    const AddressesSection = () => {
        return (<>
            {MessageBannerForAffiliation}
            <Box display='flex' width='80%' margin='60px auto 0 auto' flexDirection={{xs:'column', sm:'row'}} justifyContent='space-between'>
                <Box width={{xs:'100%', sm:'60%'}} pb={{xs:'24px', sm:'unset'}} display={'flex'} flexDirection={'column'} gap={'8px'}>
                    <Typography variant={'h2'} color='primary.main' textAlign='left' lineHeight='35px' id="headline">{PROFILE_CONTENT.addressesMenuLabel}</Typography>
                    <Typography variant={'p1'} component="div" color='neutral.main' textAlign='left' lineHeight={{xs:'21px', sm:'24px'}}  m={{xs:'16px 0', sm:'unset'}} id="headlineMessage">
                        {renderText(PROFILE_CONTENT.message)}
                        <Link to={`/${locale}/contact-us`}>
                        <StyledLink variant={'link'}>{PROFILE_CONTENT.contactUsLinkLabel}
                        </StyledLink>
                        </Link>
                    </Typography>
                </Box>
            </Box>
            <AddressesDetails addresses={sapUserData?.orgUnit?.addresses} setLoader={setLoader} />
            </>
        )
    }

    return(
        <ProfileWrapper>
            <AlertMessage variant={"filled"} type={"error"} message={errorMsg} ref={alertRef}/>
            <AlertMessage type={"info"} message={"Changes saved"} variant={"filled"} ref={successAlertRef}/>
            <NavigationPanel id="navigationPanel" data-testid='navigationPanel'>
                <Box sx={{padding: desktop ? "60px 60px 0px 60px" : "0"}}>
                    <Typography variant={'h6'} width='100%' color='neutral.white' textTransform='uppercase' id="navigationPanelMsg" data-testid='navigationPanelMsg'>
                        {PROFILE_CONTENT.menuWelcomeMessage}
                    </Typography>
                    <Typography
                        variant={'h3'}
                        color='neutral.white'
                        marginBottom={{xs:'0', sm:'40px'}}
                        sx={{ overflowWrap:'break-word' }}
                        id="navigationPanelUser"
                        data-testid='navigationPanelUser'
                    >
                        {sapUserData?.name}
                    </Typography>
                </Box>
                
                <Box display={{xs:'none', sm:'flex'}} flexDirection='column' id="navigationLinkPanel" data-testid="navigationLinkPanel">
                    <NavigationPanelLink isActive={navigationLink==="account"} id="accountDetailsLink" data-testid="accountDetailsLink" onClick={() => handlePageSelection('account')}><NavIcon src={AccountIcon} alt="account details" height="30" width="30" />{PROFILE_CONTENT.accountDetailsMenuLabel}</NavigationPanelLink>
                    <NavigationPanelLink isActive={navigationLink==="addresses"} id="addressesLink" data-testid="addressesLink" onClick={() => handlePageSelection('addresses')}><NavIcon src={AddressesIcon} alt="addresses" height="30" width="30" />{PROFILE_CONTENT.addressesMenuLabel}</NavigationPanelLink>
                    <NavigationPanelLink isActive={navigationLink==="payment-methods"} id="paymentMethodsLink" data-testid="paymentMethodsLink" onClick={() => handlePageSelection('payment-methods')}><NavIcon src={CreditCardIcon} alt="payment methods" height="30" width="30" />{PROFILE_CONTENT.paymentMethodsMenuLabel}</NavigationPanelLink>
                </Box>
            </NavigationPanel>
            <Spinner processing={loader}/>
            <AccountInfo>
                { mobile && <>
                        <Accordion
                            id="acocuntAccordion"
                            defaultExpanded={true}
                            accordionCustomStyle={accordionCustomStyle}
                            heading={ <><NavIcon src={AccountIcon} alt="account details" height="30" width="30" /> {PROFILE_CONTENT.accountDetailsMenuLabel} </>}
                            description={<AccountSection/>}
                        />
                        <Accordion
                            id="addressesAccordion"
                            accordionCustomStyle={accordionCustomStyle}
                            heading={<><NavIcon src={AddressesIcon} alt="addresses" height="30" width="30" />{PROFILE_CONTENT.addressesMenuLabel}</>}
                            description={<AddressesSection/>}
                        />
                        <Accordion
                                id="paymentMethodsAccordion"
                                accordionCustomStyle={accordionCustomStyle}
                                heading={<><NavIcon src={CreditCardIcon} alt="payment methods" height="30" width="30" />{PROFILE_CONTENT.paymentMethodsMenuLabel}</>}
                                description={<PaymentMethodsSection setProcessing={setLoader} handleErrorMsg={errorMsgHandler} />}
                        />
                </>}
                {desktop && <>
                        {navigationLink === 'account' && <AccountSection/>}
                        {navigationLink === 'addresses' && <AddressesSection/>}
                        {navigationLink === 'payment-methods' && <PaymentMethodsSection setProcessing={setLoader} handleErrorMsg={errorMsgHandler} />}
                </>}                
            </AccountInfo>
        </ProfileWrapper>
    )
}

export default ProfileComponent;